import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { FONT, RADIUS, SEMANTIC_COLOR, SPACE } from '@/shared/design-tokens/design-tokens';
import { MODAL_WIDTHS } from '@/shared/lib/constants';
import { formatHourMinute } from '@/shared/lib/format';
import { formatMetricValue } from '@/shared/lib/utils';
import { BaseModal } from '@/shared/ui/BaseModal';

import { createTooltip } from '@/widgets/monitoring/shared/createTooltip';

import styles from './MetricDetailModal.module.css';

type MetricDetailModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  primaryLabel: string;
  primaryValue: number;
  primaryHistory: { timestamp: number; value: number }[];
  primaryColor: string;
  secondaryLabel: string;
  secondaryValue: number;
  secondaryHistory: { timestamp: number; value: number }[];
  secondaryColor: string;
  unit: string;
};

type TooltipItem = {
  axisValueLabel?: string;
  color?: string;
  seriesName: string;
  value: number;
};

type SummaryMetric = {
  label: string;
  value: number;
};

const CHART_HEIGHT = 344;
const CHART_WIDTH = 736;
const AXIS_LABEL_COLOR = SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL5;
const DIVIDER_COLOR = SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL4;
const TEXT_COLOR = SEMANTIC_COLOR.ON_SURFACE.STANDARD.LEVEL1;

const getSafeAverage = (values: number[]) => {
  if (values.length === 0) {
    return 0;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const getSafeMax = (values: number[]) => {
  if (values.length === 0) {
    return 0;
  }

  return Math.max(...values);
};

const createTooltipContent = (params: TooltipItem[], unit: string) => {
  if (!params || params.length === 0) {
    return '';
  }

  const title = params[0].axisValueLabel ?? '';
  const titleHtml = title
    ? `<div style="margin-bottom: ${SPACE[8]}px; font-family: ${FONT.FAMILY.NANUMSQUARE}; font-size: ${FONT.SIZE.SM}px; line-height: ${FONT.LINE_HEIGHT[16]}px; font-weight: ${FONT.WEIGHT.REGULAR}; color: ${TEXT_COLOR};">${title}</div>`
    : '';

  const rowsHtml = params
    .map((item) => {
      return `
        <div style="display: flex; align-items: center; gap: ${SPACE[8]}px; min-height: ${SPACE[16]}px;">
          <div style="
            width: ${SPACE[8]}px;
            height: ${SPACE[8]}px;
            border-radius: ${RADIUS.FULL}px;
            background-color: ${item.color ?? TEXT_COLOR};
            flex-shrink: 0;
          "></div>
          <span style="
            font-family: ${FONT.FAMILY.NANUMSQUARE};
            font-size: ${FONT.SIZE.SM}px;
            line-height: ${FONT.LINE_HEIGHT[16]}px;
            font-weight: ${FONT.WEIGHT.REGULAR};
            color: ${TEXT_COLOR};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
            min-width: 0;
          ">${item.seriesName}</span>
          <span style="
            font-family: ${FONT.FAMILY.NANUMSQUARE};
            font-size: ${FONT.SIZE.SM}px;
            line-height: ${FONT.LINE_HEIGHT[16]}px;
            font-weight: ${FONT.WEIGHT.BOLD};
            color: ${TEXT_COLOR};
            margin-left: auto;
          ">${formatMetricValue(item.value)}${unit}</span>
        </div>
      `;
    })
    .join('');

  return `<div style="min-width: 200px;">${titleHtml}${rowsHtml}</div>`;
};

const renderSummaryMetric = (metric: SummaryMetric, unit: string) => {
  return (
    <div key={metric.label} className={styles.summaryMetric}>
      <span className={styles.summaryMetricLabel}>{metric.label}</span>
      <div className={styles.summaryMetricValueRow}>
        <span className={styles.summaryMetricValue}>{formatMetricValue(metric.value)}</span>
        <span className={styles.summaryMetricUnit}>{unit}</span>
      </div>
    </div>
  );
};

const renderLegendItem = (label: string, color: string) => {
  return (
    <div key={label} className={styles.legendItem}>
      <span className={styles.legendDot} style={{ backgroundColor: color }} />
      <span className={styles.legendLabel}>{label}</span>
    </div>
  );
};

export const MetricDetailModal = ({
  open,
  onClose,
  title,
  primaryLabel,
  primaryValue,
  primaryHistory,
  primaryColor,
  secondaryLabel,
  secondaryValue,
  secondaryHistory,
  secondaryColor,
  unit,
}: MetricDetailModalProps) => {
  const times = primaryHistory.map((point) => formatHourMinute(point.timestamp));
  const primaryValues = primaryHistory.map((point) => point.value);
  const secondaryValues = secondaryHistory.map((point) => point.value);

  const primarySummaryMetrics: SummaryMetric[] = [
    { label: `현재 ${primaryLabel}`, value: primaryValue },
    { label: `최대 ${primaryLabel}`, value: getSafeMax(primaryValues) },
    { label: `평균 ${primaryLabel}`, value: getSafeAverage(primaryValues) },
  ];

  const secondarySummaryMetrics: SummaryMetric[] = [
    { label: `현재 ${secondaryLabel}`, value: secondaryValue },
    { label: `최대 ${secondaryLabel}`, value: getSafeMax(secondaryValues) },
    { label: `평균 ${secondaryLabel}`, value: getSafeAverage(secondaryValues) },
  ];

  const chartOption: EChartsOption = {
    animation: false,
    grid: {
      left: 0,
      right: 0,
      top: 8,
      bottom: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: times,
      axisLine: {
        show: true,
        lineStyle: {
          color: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL3,
        },
      },
      axisLabel: {
        interval: Math.floor((times.length - 1) / 4),
        align: 'left',
        color: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL5,
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontSize: FONT.SIZE.SM,
        hideOverlap: false,
        margin: 12,
      },
    },
    yAxis: {
      type: 'value',
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: AXIS_LABEL_COLOR,
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontSize: FONT.SIZE.SM,
        formatter: (value: number) => formatMetricValue(value),
        margin: 12,
        width: 24,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: DIVIDER_COLOR,
          type: 'dashed',
          width: 1,
        },
      },
    },
    series: [
      {
        name: primaryLabel,
        type: 'line',
        data: primaryValues,
        smooth: false,
        symbol: 'none',
        showSymbol: false,
        connectNulls: false,
        clip: true,
        lineStyle: {
          color: primaryColor,
          width: 1,
          opacity: 0.92,
        },
        itemStyle: {
          color: primaryColor,
        },
        emphasis: {
          disabled: true,
        },
      },
      {
        name: secondaryLabel,
        type: 'line',
        data: secondaryValues,
        smooth: false,
        symbol: 'none',
        showSymbol: false,
        connectNulls: false,
        clip: true,
        lineStyle: {
          color: secondaryColor,
          width: 1,
          opacity: 0.92,
        },
        itemStyle: {
          color: secondaryColor,
        },
        emphasis: {
          disabled: true,
        },
      },
    ],
    tooltip: {
      ...(createTooltip(unit) as EChartsOption['tooltip']),
      formatter: (params: unknown) => {
        return createTooltipContent(params as TooltipItem[], unit);
      },
    },
  };

  return (
    <BaseModal
      title={title}
      open={open}
      onClose={onClose}
      width={MODAL_WIDTHS.LARGE_PANEL}
      height={600}
      footer={null}
      centered>
      <div className={styles.content}>
        <div className={styles.summaryPanel}>
          <div className={styles.summaryGroup}>
            <span className={styles.primarySummaryTitle}>{primaryLabel}</span>
            <div className={styles.summaryMetricsRow}>
              {primarySummaryMetrics.map((metric) => renderSummaryMetric(metric, unit))}
            </div>
          </div>

          <div className={styles.summaryDivider} />

          <div className={styles.summaryGroup}>
            <span className={styles.secondarySummaryTitle}>{secondaryLabel}</span>
            <div className={styles.summaryMetricsRow}>
              {secondarySummaryMetrics.map((metric) => renderSummaryMetric(metric, unit))}
            </div>
          </div>
        </div>

        <div className={styles.infoRow}>
          <span className={styles.infoText}>최근 30분간의 데이터 (5초 간격)</span>
          <div className={styles.legendRow}>
            {renderLegendItem(primaryLabel, primaryColor)}
            {renderLegendItem(secondaryLabel, secondaryColor)}
          </div>
        </div>

        <div className={styles.chartContainer}>
          <ReactECharts
            option={chartOption}
            style={{ height: CHART_HEIGHT, width: CHART_WIDTH }}
            opts={{ renderer: 'canvas' }}
            notMerge={true}
            lazyUpdate={false}
          />
        </div>
      </div>
    </BaseModal>
  );
};
